import { AxiosError } from "axios";
import { CacheAxiosResponse } from "axios-cache-interceptor";
import { useMemo } from "react";
import { BaseService } from "../../../../../shared/src/services";
import { getDefaultAxiosInstance } from "../../../../../shared/src/services/BaseService/AxiosService";
import { EUFinanceFileStoreDeleteFileMetadata } from "../../models/metadata/EUFinanceFilestoreDeleteFileMetadata";
import { EUFinanceFileStoreGetFileMetadata } from "../../models/metadata/EUFinanceFilestoreGetFileMetadata";
import { EUFinanceFileStoreSaveFileMetadata } from "../../models/metadata/EUFinanceFilestoreSaveFileMetadata";
import { EUFinanceFileStoreDeleteFileResponse } from "../../models/response/EUFinanceFileStoreDeleteFileResponse";
import { EUFinanceFileStoreGetFileResponse } from "../../models/response/EUFinanceFileStoreGetFileResponse";
import { EUFinanceFileStoreSaveFileResponse } from "../../models/response/EUFinanceFileStoreSaveFileResponse";

class EUFinanceFileStoreService extends BaseService {
  constructor() {
    super(getDefaultAxiosInstance(), "/api/myemory/v0/eufinance/filestore");
  }

  public async getAllowedMimeTypeToExtensionsMap(): Promise<
    Record<string, string[]>
  > {
    try {
      const response = await this.get<Record<string, string[]>>({
        url: this.getPath("/allowed-mime-type-to-extensions-map"),
      });
      return response.data ?? {};
    } catch (error) {
      throw new Error("Failed to get allowed mime type to extensions map");
    }
  }

  public async saveFileByKeyAndMetadata(
    key: string,
    file: File,
    metadata: EUFinanceFileStoreSaveFileMetadata
  ): Promise<EUFinanceFileStoreSaveFileResponse> {
    const formData = new FormData();
    formData.append("files", file);
    formData.append("metadata", JSON.stringify(metadata));
    try {
      const response = await this.post<EUFinanceFileStoreSaveFileResponse>({
        url: this.getPath(key),
        data: formData,
        config: {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      });
      return response.data;
    } catch (error) {
      throw new Error("Failed to upload files");
    }
  }

  public async getFileByKeyAndMetadata(
    key: string,
    metadata: EUFinanceFileStoreGetFileMetadata
  ): Promise<EUFinanceFileStoreGetFileResponse> {
    const metadataString = JSON.stringify(metadata);
    try {
      const response = await this.get<Blob>({
        url: this.getPath(
          `${key}?metadata=${encodeURIComponent(metadataString)}`
        ),
        config: {
          responseType: "blob",
        },
      });
      return {
        content: response.data,
        metadata: {
          fileName: metadata.fileName,
          filePath: metadata.filePath,
        },
      };
    } catch (error) {
      throw new Error("Failed to get file");
    }
  }

  public async deleteFileByKeyAndMetadata(
    key: string,
    metadata: EUFinanceFileStoreDeleteFileMetadata
  ): Promise<CacheAxiosResponse<EUFinanceFileStoreDeleteFileResponse>> {
    const metadataString = JSON.stringify(metadata);
    try {
      const response = await this.delete<EUFinanceFileStoreDeleteFileResponse>({
        url: this.getPath(
          `${key}?metadata=${encodeURIComponent(metadataString)}`
        ),
      });
      return response;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          `Failed to delete file: ${
            error.response.data?.message || error.message
          }`
        );
      } else {
        throw new Error(
          `Failed to delete file: ${
            error instanceof Error ? error.message : "Unknown error"
          }`
        );
      }
    }
  }
}

export default EUFinanceFileStoreService;

export const useEUFinanceFileStoreService = (): EUFinanceFileStoreService =>
  useMemo(() => new EUFinanceFileStoreService(), []);
